/**
 * useRoomActivity.ts
 * Track room read state and detect new activity (#1180, #1611)
 *
 * Implements ADR-UX-004 "Lantern Glow" pattern with the v1.4 evolution:
 * - Per-room `lastReadAt` (Unix seconds) persisted via the platform seam in
 *   `lib/roomReadState` (web: localStorage, native: injected AsyncStorage —
 *   see `roomReadState.native.ts`, #2066)
 * - Mark-as-read fires on scroll-to-bottom-with-dwell + send + switch-with-bottom
 *   (NOT on bare entry — that was the MVP behavior and would defeat the unread
 *   divider added in v1.4)
 * - Binary activity state (new activity vs quiet) — never message counts
 *
 * Reactive: state is owned by useState so writes propagate within the same
 * React tree. The hook should be called in a SINGLE owner (ClubChatPage) and
 * its utilities passed down — calling the hook in two siblings yields two
 * independent state copies. See feat/1611-chat-read-state spec for context.
 *
 * Hydration: web storage is synchronous, so `isHydrated` is `true` from the
 * first render and behavior is identical to the pre-seam hook. Native storage
 * is async — the hook starts empty and hydrates via a functional merge
 * (per-room max), so `markAsRead` calls that land pre-hydration are never
 * lost or regressed regardless of updater/promise interleaving. Consumers
 * MUST gate unread-divider placement on `isHydrated` or every message
 * flashes as unread on native cold start.
 *
 * Persistence topology (trio P2, 2026-07-02): state updaters are PURE —
 * persistence happens in exactly one effect that only observes committed,
 * post-hydration snapshots. That makes the seam's "full map, never partial,
 * never pre-hydration" write contract structural rather than a convention
 * spread across call sites.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  loadRoomReadState,
  loadRoomReadStateSync,
  saveRoomReadState,
  type RoomViewState,
} from '../lib/roomReadState';

// Recent activity threshold — 5 minutes for "happening now" pulse
const RECENT_ACTIVITY_SECONDS = 5 * 60;

interface ReadStateSnapshot {
  state: RoomViewState;
  /** True once `state` reflects persisted storage (sync-immediate on web). */
  hydrated: boolean;
  /** True when `state` holds markers the persistence effect hasn't written. */
  dirty: boolean;
}

function initialSnapshot(): ReadStateSnapshot {
  const sync = loadRoomReadStateSync();
  return sync !== null
    ? { state: sync, hydrated: true, dirty: false } // web: exactly as before
    : { state: {}, hydrated: false, dirty: false }; // native: hydrate in effect
}

/** Per-room max merge — never regresses a marker in either direction. */
function mergePerRoomMax(
  base: RoomViewState,
  overlay: RoomViewState
): RoomViewState {
  const merged: RoomViewState = { ...base };
  for (const [roomId, ts] of Object.entries(overlay)) {
    if ((merged[roomId] ?? 0) < ts) merged[roomId] = ts;
  }
  return merged;
}

/**
 * Hook for room read-state + activity detection.
 *
 * @example
 * ```tsx
 * const { hasNewActivity, isRecentlyActive, markAsRead, getLastReadAt, isHydrated } = useRoomActivity();
 *
 * // Glow indicator on the room list
 * if (hasNewActivity(room)) showGlow();
 *
 * // Where to land on first-load + whether to render the unread divider —
 * // wait for isHydrated before trusting getLastReadAt on native
 * const lastReadAt = getLastReadAt(roomId);
 *
 * // Mark room as read after dwell-at-bottom / send / switch-with-bottom
 * markAsRead(roomId);
 * ```
 */
export function useRoomActivity() {
  const [snapshot, setSnapshot] = useState<ReadStateSnapshot>(initialSnapshot);

  // Async hydration (native). On web the functional updater bails on the
  // already-hydrated snapshot (same reference → no re-render). The `.catch`
  // is defensive: both seam impls document a never-rejects contract, but a
  // rejection here would otherwise freeze isHydrated at false and drop a
  // whole session of markers (hunter P3-1).
  useEffect(() => {
    let cancelled = false;
    loadRoomReadState()
      .catch((): RoomViewState => ({}))
      .then((persisted) => {
        if (cancelled) return;
        setSnapshot((prev) => {
          if (prev.hydrated) return prev;
          // Functional merge: any marker set before this updater runs is in
          // prev.state, so no interleaving of dispatch/microtask order can
          // lose it. dirty carries forward so the persistence effect writes
          // the merged map exactly when pre-hydration markers existed.
          const hadPending = Object.keys(prev.state).length > 0;
          return {
            state: mergePerRoomMax(persisted, prev.state),
            hydrated: true,
            dirty: prev.dirty || hadPending,
          };
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The ONLY persistence point. Observes committed snapshots, writes the
  // full map, and is structurally incapable of a pre-hydration or partial
  // write. Plain hydration (dirty=false) never writes. Idempotent under
  // StrictMode double-invoke (same full map).
  useEffect(() => {
    if (!snapshot.hydrated || !snapshot.dirty) return;
    saveRoomReadState(snapshot.state);
  }, [snapshot]);

  /**
   * Mark a room as read at the timestamp of the latest message the user has
   * actually rendered. Callers MUST pass the latest rendered server message's
   * `created_at` (Unix seconds), not wall-clock time.
   *
   * Why not `Date.now()`: client wall-clock can race ahead of unfetched server
   * messages. If a server message exists at t=12:00:08 that hasn't been polled
   * into our view yet, marking-read at wall-clock 12:00:10 would hide it
   * permanently because `last_message_at (12:00:08) <= lastReadAt (12:00:10)`.
   * Anchoring to latest rendered means the marker can never advance past what
   * the user has actually seen — unfetched messages always show as unread on
   * the next refresh. (Frank #1613 P1.)
   */
  const markAsRead = useCallback((roomId: string, atTimestamp: number) => {
    setSnapshot((prev) => {
      // Don't regress: if the user already has a later marker (e.g. they
      // marked read in another tab/component), keep it.
      const existing = prev.state[roomId];
      if (existing !== undefined && existing >= atTimestamp) return prev;
      return {
        ...prev,
        state: { ...prev.state, [roomId]: atTimestamp },
        dirty: true,
      };
    });
  }, []);

  /**
   * Return the room's last-read timestamp (Unix seconds), or undefined if
   * the user has never marked the room read on this device.
   */
  const getLastReadAt = useCallback(
    (roomId: string): number | undefined => snapshot.state[roomId],
    [snapshot.state]
  );

  /**
   * Binary check: does this room have new activity since the user last read it?
   * Returns true when:
   * - Room has any messages (last_message_at present), AND
   * - Either user has never marked the room read OR last_message_at > lastReadAt
   */
  const hasNewActivity = useCallback(
    (room: { id: string; last_message_at: number | null }): boolean => {
      if (!room.last_message_at) return false;
      const lastRead = snapshot.state[room.id];
      if (!lastRead) return true;
      return room.last_message_at > lastRead;
    },
    [snapshot.state]
  );

  /**
   * Check if room has very recent activity (within last 5 minutes).
   * Used for the "breathing/pulse" animation indicating live conversation.
   */
  const isRecentlyActive = useCallback(
    (room: { last_message_at: number | null }): boolean => {
      if (!room.last_message_at) return false;
      const now = Math.floor(Date.now() / 1000);
      return now - room.last_message_at < RECENT_ACTIVITY_SECONDS;
    },
    []
  );

  return {
    hasNewActivity,
    isRecentlyActive,
    markAsRead,
    getLastReadAt,
    /**
     * False until persisted read-state has loaded (async on native; always
     * true on web). Gate unread-divider placement on this.
     */
    isHydrated: snapshot.hydrated,
  };
}
